<template>
    <Card>
        <CardHeader>
            <div class="flex items-center gap-2">
                <FileText class="size-4 text-muted-foreground" />
                <CardTitle class="text-sm font-medium text-primary">
                    Billing information
                </CardTitle>
            </div>
        </CardHeader>

        <CardContent>
            <form class="space-y-6" @submit.prevent="save">
                <div class="space-y-5 max-w-[400px]">
                    <div>
                        <Label class="text-sm font-medium text-foreground">Name and Surname *</Label>
                        <Input v-model="form.name" class="mt-2" required />
                    </div>
                    <div>
                        <Label class="text-sm font-medium text-foreground">E-mail address *</Label>
                        <Input v-model="form.email" type="email" class="mt-2" required />
                    </div>
                    <div>
                        <Label class="text-sm font-medium text-foreground">Company</Label>
                        <Input v-model="form.company" class="mt-2" placeholder="Enter company name" />
                    </div>
                    <div>
                        <Label class="text-sm font-medium text-foreground">Tax ID</Label>
                        <Input v-model="form.tax_id" class="mt-2" />
                    </div>
                    <div>
                        <Label class="text-sm font-medium text-foreground">CRN</Label>
                        <Input v-model="form.crn" class="mt-2" />
                    </div>
                    <div>
                        <Label class="text-sm font-medium text-foreground">Address</Label>
                        <Input v-model="form.street" class="mt-2" />
                    </div>
                    <div class="flex gap-4">
                        <div class="flex-1">
                            <Label class="text-sm font-medium text-foreground">City</Label>
                            <Input v-model="form.city" class="mt-2" />
                        </div>
                        <div class="flex-1">
                            <Label class="text-sm font-medium text-foreground">ZIP / Postal code</Label>
                            <Input v-model="form.postal_code" class="mt-2" />
                        </div>
                    </div>
                    <div>
                        <Label class="text-sm font-medium text-foreground">Country</Label>
                        <Select v-model="form.country">
                            <SelectTrigger class="mt-2">
                                <SelectValue placeholder="Select country" />
                            </SelectTrigger>
                            <SelectContent :body-lock="false">
                                <SelectItem
                                    v-for="country in countries"
                                    :key="country.value"
                                    :value="country.value"
                                >
                                    {{ country.title }}
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <Button type="submit" :disabled="loading">
                    Save changes
                </Button>
            </form>
        </CardContent>
    </Card>
</template>

<script lang="ts" setup>
import { ref, PropType } from 'vue';
import { router } from '@inertiajs/vue3';
import { route } from 'ziggy-js';
import { toast } from 'vue-sonner';
import { FileText } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import BillingAddress from '@/types/Subscription/BillingAddress';

const props = defineProps({
    billingAddress: {
        type: Object as PropType<BillingAddress>,
        required: true,
    },
    countries: {
        type: Array as PropType<Array<{ value: string; title: string }>>,
        required: true,
    },
});

const loading = ref(false);

const form = ref({
    name: props.billingAddress.name || '',
    email: props.billingAddress.email || '',
    company: props.billingAddress.company || '',
    tax_id: props.billingAddress.tax_id || '',
    crn: props.billingAddress.crn || '',
    street: props.billingAddress.street || '',
    city: props.billingAddress.city || '',
    postal_code: props.billingAddress.postal_code || '',
    country: props.billingAddress.country || '',
});

const save = () => {
    loading.value = true;

    router.put(
        route('billing-addresses.update-plugin'),
        form.value,
        {
            preserveScroll: true,
            onSuccess: () => {
                toast.success('Billing information updated');
            },
            onError: (err) => {
                const msg = Object.values(err)[0] as string || 'Failed to update billing information';
                toast.error(msg);
            },
            onFinish: () => {
                loading.value = false;
            },
        }
    );
};
</script>
